import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import { CustomerOption, searchCustomerOptions } from '../../services/request/Borrowers';

export interface PickedCustomer {
  id: string;
  name: string;
}

interface CustomerPickerProps {
  label: string;
  value: PickedCustomer | null;
  onChange: (customer: PickedCustomer | null) => void;
  /** Customers who cannot be chosen, such as the borrower themselves. */
  excludeIds?: Array<string | null | undefined>;
  /** Guarantors carry liability, so blacklisted customers are not selectable. */
  disallowBlacklisted?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
}

const SEARCH_DELAY_MS = 250;

const CustomerPicker: React.FC<CustomerPickerProps> = ({
  label,
  value,
  onChange,
  excludeIds = [],
  disallowBlacklisted = false,
  helperText,
  size = 'small',
  disabled,
}) => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<CustomerOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    const timer = setTimeout(() => {
      searchCustomerOptions(input)
        .then((found) => active && setOptions(found))
        .catch(() => active && setOptions([]))
        .finally(() => active && setLoading(false));
    }, SEARCH_DELAY_MS);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [input, open]);

  const excluded = new Set(excludeIds.filter(Boolean) as string[]);
  const selectable = options.filter((o) => !excluded.has(o.id));
  const selected: CustomerOption | null = value
    ? selectable.find((o) => o.id === value.id) ?? { id: value.id, name: value.name, blacklisted: false }
    : null;

  return (
    <Autocomplete<CustomerOption, false, false, false>
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={selected}
      onChange={(_, option) => onChange(option ? { id: option.id, name: option.name } : null)}
      inputValue={input}
      onInputChange={(_, text, reason) => reason !== 'reset' && setInput(text)}
      options={selectable}
      filterOptions={(items) => items}
      isOptionEqualToValue={(option, current) => option.id === current.id}
      getOptionLabel={(option) => option.name}
      getOptionDisabled={(option) => disallowBlacklisted && option.blacklisted}
      loading={loading}
      disabled={disabled}
      noOptionsText={input ? 'No matching customer' : 'Type a name, phone or email'}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {option.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {[option.borrowerId, option.contactNumber, option.email].filter(Boolean).join(' · ') || '—'}
            </Typography>
          </Box>
          {option.blacklisted && <Chip label="Blacklisted" size="small" color="error" variant="outlined" />}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size={size}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default CustomerPicker;
